/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "onedial_cookie_consent";
const VERSION = 1;

/**
 * GDPR-friendly cookie banner with granular categories.
 * Stores: { v, ts, necessary: true, preferences: bool, analytics: bool, marketing: bool }
 */
export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [prefs, setPrefs] = useState({ preferences: false, analytics: false, marketing: false });

  useEffect(() => {
    try {
      const existing = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "null");
      if (!existing || existing.v !== VERSION) {
        setOpen(true);
      } else {
        // Re-emit on mount so analytics scripts can hydrate.
        emit(existing);
      }
    } catch { setOpen(true); }
  }, []);

  function emit(consent) {
    try {
      window.dispatchEvent(new CustomEvent("onedial:cookie-consent", { detail: consent }));
    } catch (err) {
      // Safari/iframe edge case where CustomEvent dispatch is blocked. Log but
      // do not block the user — the consent state is already persisted.
      if (process.env.NODE_ENV !== "production") {
        console.warn("cookie-consent dispatch failed", err);
      }
    }
  }

  function save(next) {
    const payload = { v: VERSION, ts: new Date().toISOString(), necessary: true, ...next };
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (err) {
      // localStorage may be blocked (private mode / quota). Falling back to
      // an in-memory state for this tab is acceptable.
      if (process.env.NODE_ENV !== "production") {
        console.warn("cookie-consent persist failed", err);
      }
    }
    emit(payload);
    setOpen(false);
  }

  if (!open) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6 sm:pb-6" data-testid="cookie-banner">
      <div className="mx-auto max-w-3xl rounded-2xl bg-white border border-brand-line shadow-xl p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <Cookie className="h-6 w-6 text-brand-greenDark mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-display font-extrabold text-base">We use cookies</h3>
            <p className="mt-1 text-sm text-brand-muted">
              Strictly necessary cookies keep you signed in. We'd love your consent to use optional cookies for preferences, analytics and marketing so we can make OneDial better. Read our <Link to="/cookie-policy" className="text-brand-greenDark underline">Cookie Policy</Link>.
            </p>
            <p className="mt-1.5 text-[11px] text-brand-muted/80">
              Data controller: <span className="font-semibold text-brand-ink/85">Scitygate Telecom UK Ltd</span> · Co. No. 16468736 · Manchester, UK.
            </p>
          </div>
          <button type="button" onClick={() => save({ preferences: false, analytics: false, marketing: false })}
            data-testid="cookie-dismiss" aria-label="Dismiss"
            className="rounded-full p-1.5 hover:bg-brand-bgSoft">
            <X className="h-4 w-4" />
          </button>
        </div>

        {showAdvanced && (
          <div className="mt-4 space-y-2 text-sm">
            <CategoryRow label="Strictly necessary" desc="Always on — required for sign-in, security, payments." checked disabled />
            <CategoryRow label="Preferences" desc="Remember your settings (locale, country, dismissed tips)."
              checked={prefs.preferences} onChange={(v) => setPrefs({...prefs, preferences: v})} testid="cookie-pref-preferences" />
            <CategoryRow label="Analytics" desc="Anonymous usage statistics so we can improve OneDial (Google Analytics)."
              checked={prefs.analytics} onChange={(v) => setPrefs({...prefs, analytics: v})} testid="cookie-pref-analytics" />
            <CategoryRow label="Marketing" desc="Measure effectiveness of our ads to avoid wasting your time."
              checked={prefs.marketing} onChange={(v) => setPrefs({...prefs, marketing: v})} testid="cookie-pref-marketing" />
          </div>
        )}

        <div className="mt-5 flex flex-wrap gap-2">
          <button type="button" onClick={() => save({ preferences: true, analytics: true, marketing: true })}
            data-testid="cookie-accept-all"
            className="rounded-full bg-brand-green hover:bg-brand-greenHover text-white px-5 py-2 text-sm font-semibold">
            Accept all
          </button>
          <button type="button" onClick={() => save({ preferences: false, analytics: false, marketing: false })}
            data-testid="cookie-reject-all"
            className="rounded-full bg-white border border-brand-line hover:bg-brand-bgSoft text-brand-ink px-5 py-2 text-sm font-semibold">
            Reject optional
          </button>
          {!showAdvanced ? (
            <button type="button" onClick={() => setShowAdvanced(true)} data-testid="cookie-customize"
              className="ml-auto rounded-full text-brand-greenDark hover:bg-brand-greenSoft px-4 py-2 text-sm font-semibold">
              Customise
            </button>
          ) : (
            <button type="button" onClick={() => save(prefs)} data-testid="cookie-save-prefs"
              className="ml-auto rounded-full bg-brand-ink hover:bg-brand-ink/90 text-white px-5 py-2 text-sm font-semibold">
              Save preferences
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CategoryRow({ label, desc, checked, onChange, disabled = false, testid }) {
  return (
    <label className={`flex items-start gap-3 rounded-lg border p-3 ${disabled ? "border-brand-line bg-brand-bgSoft opacity-80" : "border-brand-line"}`}>
      <input
        type="checkbox" checked={checked} disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        data-testid={testid}
        className="mt-1 h-4 w-4 rounded border-brand-line text-brand-green focus:ring-brand-green"
      />
      <span className="text-sm">
        <span className="font-semibold block">{label}</span>
        <span className="text-brand-muted">{desc}</span>
      </span>
    </label>
  );
}
